import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { createUserEmail, deleteUserEmail, getApiErrorMessage, verifyUserEmail } from "@/api";
import { ConfirmDialog } from "@/components/dialog/confirm/ConfirmDialog";
import * as Dialog from "@/components/dialog/DialogLayout";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError, FormFieldHint } from "@/components/form/FormField";
import { FormInput } from "@/components/form/FormInput";
import { validateTeamEmail } from "@/models/api/TeamApi";
import { useStore } from "@/store";
import { EMAIL_VERIFICATION_CODE_LENGTH } from "@/store/user/userUtils";
import { twMerge } from "@/utils/twMerge";

import type { UserEmail } from "@/api";
import type { KeyboardEvent } from "react";

/**
 * The section renders inside the account <form> (nested forms are invalid),
 * so Enter must act locally instead of submitting the outer form.
 */
function actOnEnter(action: () => void) {
  return (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };
}

function statusLabel(userEmail: UserEmail) {
  if (userEmail.kind === "primary") return "userEmails.statusPrimary";
  if (userEmail.verified) return "userEmails.statusVerified";
  return "userEmails.statusUnverified";
}

const EmailRow = observer(function EmailRow({
  userEmail,
  showStatus,
  onRemove,
}: {
  userEmail: UserEmail;
  showStatus: boolean;
  onRemove: () => void;
}) {
  const t = useTranslations();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeResent, setCodeResent] = useState(false);

  const isPrimary = userEmail.kind === "primary";

  const canVerify = code.trim().length >= EMAIL_VERIFICATION_CODE_LENGTH;

  const handleVerify = async () => {
    if (!canVerify || isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await verifyUserEmail(userEmail.uid, { code: code.trim() });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  const handleResend = async () => {
    setIsBusy(true);
    setError(null);
    setCodeResent(false);
    try {
      await createUserEmail({ email: userEmail.email });
      setCodeResent(true);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <li className="flex flex-col mb-3">
      <div className="flex items-center gap-3 min-h-8">
        <span className="text-size-16 tracking-tight truncate">{userEmail.email}</span>
        {showStatus && (
          <span className="text-[13px] text-gray-500 shrink-0" data-testid="email-status">
            {t(statusLabel(userEmail))}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 shrink-0">
          {!isPrimary && (
            <button
              type="button"
              className="text-[13px] text-gray-500 hover:text-error"
              onClick={onRemove}
            >
              {t("userEmails.remove")}
            </button>
          )}
        </div>
      </div>
      {!userEmail.verified && (
        <>
          <div className="flex items-center gap-2 mt-2">
            <FormInput
              placeholder={t("userEmails.codeInput.placeholder")}
              aria-label={t("userEmails.codeInput.placeholder")}
              inputMode="numeric"
              autoComplete="one-time-code"
              data-1p-ignore="true"
              data-bwignore="true"
              data-lpignore="true"
              maxLength={EMAIL_VERIFICATION_CODE_LENGTH}
              className="dark:bg-gray-800"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeResent(false);
              }}
              onKeyDown={actOnEnter(handleVerify)}
            />
            <FormButton
              size="sm"
              variant="accent"
              className="h-11 shrink-0"
              isBusy={isBusy}
              disabled={!canVerify}
              onClick={handleVerify}
            >
              {t("userEmails.verify")}
            </FormButton>
            <button
              type="button"
              className="text-[13px] text-gray-500 underline shrink-0"
              onClick={handleResend}
            >
              {t("userEmails.resend")}
            </button>
          </div>
          <FormFieldHint
            message={codeResent ? t("userEmails.codeResent") : t("userEmails.codeSentHint")}
          />
        </>
      )}
      <FormFieldError message={error} />
    </li>
  );
});

const AddEmailForm = observer(function AddEmailForm() {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setIsOpen(false);
    setValue("");
    setError(null);
  };

  const handleAdd = async () => {
    const email = value.trim();
    if (!email || isBusy) return;

    const validationError = validateTeamEmail(email);
    if (validationError) {
      setError(t(validationError));
      return;
    }

    setIsBusy(true);
    setError(null);
    try {
      await createUserEmail({ email });
      close();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        className="text-[13px] text-blue-500 hover:underline mr-auto mt-1"
        onClick={() => setIsOpen(true)}
      >
        {t("userEmails.addToggle")}
      </button>
    );
  }

  return (
    <div className="flex flex-col mt-1">
      <div className="flex items-center gap-2">
        <FormInput
          type="email"
          placeholder={t("userEmails.addInput.placeholder")}
          aria-label={t("userEmails.addInput.placeholder")}
          autoComplete="off"
          data-1p-ignore="true"
          data-bwignore="true"
          data-lpignore="true"
          className="dark:bg-gray-800"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={actOnEnter(handleAdd)}
        />
        <FormButton
          size="sm"
          variant="accent"
          className="h-11 shrink-0"
          isBusy={isBusy}
          disabled={!value.trim()}
          onClick={handleAdd}
        >
          {t("userEmails.addButton")}
        </FormButton>
        <button type="button" className="text-[13px] text-gray-500 shrink-0" onClick={close}>
          {t("dialog.cancel")}
        </button>
      </div>
      <FormFieldError message={error} />
      <FormFieldHint message={t("userEmails.addHint")} />
    </div>
  );
});

export const UserEmailsSection = observer(function UserEmailsSection({
  className,
}: {
  className?: string;
}) {
  const t = useTranslations();
  const { auth } = useStore();
  const emails = auth.currentUser?.emails ?? [];
  const sortedEmails = [
    ...emails.filter((email) => email.kind === "primary"),
    ...emails.filter((email) => email.kind !== "primary"),
  ];

  const [removeCandidate, setRemoveCandidate] = useState<UserEmail | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!removeCandidate) return;

    setIsRemoving(true);
    setRemoveError(null);
    try {
      await deleteUserEmail(removeCandidate.uid);
      setRemoveCandidate(null);
    } catch (err) {
      setRemoveError(getApiErrorMessage(err));
      setRemoveCandidate(null);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className={twMerge("flex flex-col", className)}>
      <Dialog.Subtitle className="text-gray-500 mb-3">{t("userEmails.title")}</Dialog.Subtitle>
      <ul className="flex flex-col">
        {sortedEmails.map((userEmail) => (
          <EmailRow
            key={userEmail.uid}
            userEmail={userEmail}
            showStatus={emails.length > 1}
            onRemove={() => setRemoveCandidate(userEmail)}
          />
        ))}
      </ul>
      <FormFieldError message={removeError} />
      <AddEmailForm />
      <ConfirmDialog
        title={t("userEmails.removeConfirmTitle", { email: removeCandidate?.email ?? "" })}
        isOpen={!!removeCandidate}
        isLoading={isRemoving}
        onConfirm={handleRemove}
        onClose={() => setRemoveCandidate(null)}
      />
    </div>
  );
});
